import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  ActivityIndicator
} from 'react-native';
import tw from 'twrnc';

import { useAuth } from '../../../shared/hooks/useAuth';
import { paymentService } from '../services/paymentService';
import { subscriptionStateManager } from '../services/subscriptionStateManager';
import { SubscriptionPlan } from '../types/premiumFeatures';

import { ConfirmationModal } from './ConfirmationModal';
import { clearFeatureAccessCache } from './PremiumFeatureGate';
import { uiText } from '../../../shared/utils/uiText';

interface BillingIntervalCardProps {
  currentPlan: SubscriptionPlan;
  currentBillingInterval: 'monthly' | 'annual';
  targetBillingInterval: 'monthly' | 'annual';
  onChangeComplete?: () => void;
}

export const BillingIntervalCard: React.FC<BillingIntervalCardProps> = ({
  currentPlan,
  currentBillingInterval,
  targetBillingInterval,
  onChangeComplete
}) => {
  console.log('🔄 [BillingIntervalCard] Rendering with props:', {
    currentPlan: currentPlan?.name,
    currentBillingInterval,
    targetBillingInterval
  });

  const [changing, setChanging] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { user } = useAuth();

  const isUpgradeToAnnual = currentBillingInterval === 'monthly' && targetBillingInterval === 'annual';
  const isDowngradeToMonthly = currentBillingInterval === 'annual' && targetBillingInterval === 'monthly';

  const currentPrice = currentPlan.price[currentBillingInterval];
  const targetPrice = currentPlan.price[targetBillingInterval];

  // Calculate annual savings
  const annualSavings = isUpgradeToAnnual
    ? Math.round(((currentPlan.price.monthly * 12 - currentPlan.price.annual) / (currentPlan.price.monthly * 12)) * 100)
    : 0;

  // Calculate monthly equivalent for annual
  const monthlyEquivalent = targetBillingInterval === 'annual'
    ? (currentPlan.price.annual / 12).toFixed(2)
    : null;

  const handleBillingChange = async () => {
    console.log('🔄 [BillingIntervalCard] Button clicked!', {
      user: user?.id,
      currentBillingInterval,
      targetBillingInterval,
      currentPlan: currentPlan?.name,
      hasCurrentPlan: !!currentPlan
    });

    if (!user?.id) {
      console.error('❌ [BillingIntervalCard] No user ID found');
      Alert.alert(uiText('errorTitle'), uiText('userNotFoundSignIn'));
      return;
    }

    if (!currentPlan) {
      console.error('❌ [BillingIntervalCard] Missing current plan data');
      Alert.alert(uiText('errorTitle'), uiText('planInfoMissing'));
      return;
    }

    console.log('🔄 [BillingIntervalCard] About to show confirmation modal');

    // Show the premium confirmation modal
    setShowConfirmModal(true);
  };

  const performBillingChange = async () => {
    try {
      setChanging(true);

      console.log(`🔄 [BillingChange] Changing billing from ${currentBillingInterval} to ${targetBillingInterval}`);

      const result = await paymentService.changeSubscriptionPlan({
        userId: user!.id,
        newPlanId: currentPlan.id,
        newBillingInterval: targetBillingInterval,
        prorationBehavior: 'immediate'
      });

      if (result.success) {
        // Clear caches and trigger state refresh
        clearFeatureAccessCache(user!.id);
        await subscriptionStateManager.refreshAllSubscriptionData();

        Alert.alert(
          uiText('billingUpdatedTitle'),
          isUpgradeToAnnual ? uiText('billingSwitchedAnnual', { percent: annualSavings }) : uiText('billingSwitchedMonthly'),
          [
            {
              text: uiText('great'),
              onPress: onChangeComplete
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Billing change failed');
      }
    } catch (error) {
      console.error('❌ [BillingChange] Billing change failed:', error);
      Alert.alert(
        uiText('billingChangeFailedTitle'),
        error instanceof Error ? error.message : uiText('billingChangeFailedMessage'),
        [{ text: uiText('ok') }]
      );
    } finally {
      setChanging(false);
    }
  };

  return (
    <View style={tw`bg-white border rounded-xl p-4 mb-4 ${isUpgradeToAnnual ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
      {/* Header */}
      <View style={tw`flex-row items-center justify-between mb-3`}>
        <View style={tw`flex-row items-center`}>
          <Ionicons
            name="calendar-outline"
            size={24}
            color={isUpgradeToAnnual ? "#10B981" : "#3B82F6"}
          />
          <Text style={tw`text-lg font-bold ml-2 ${isUpgradeToAnnual ? 'text-green-800' : 'text-blue-800'}`}>
            Switch to {targetBillingInterval === 'annual' ? 'Annual' : 'Monthly'} Billing
          </Text>
        </View>
      </View>

      {/* Current vs New Billing */}
      <View style={tw`mb-4`}>
        <View style={tw`flex-row justify-between items-center mb-2`}>
          <Text style={tw`text-gray-600`}>Current Billing:</Text>
          <Text style={tw`font-semibold`}>${currentPrice}/{currentBillingInterval === 'monthly' ? 'month' : 'year'}</Text>
        </View>
        <View style={tw`flex-row justify-between items-center`}>
          <Text style={tw`text-gray-600`}>New Billing:</Text>
          <View style={tw`items-end`}>
            <Text style={tw`font-semibold`}>${targetPrice}/{targetBillingInterval === 'monthly' ? 'month' : 'year'}</Text>
            {monthlyEquivalent && (
              <Text style={tw`text-sm text-gray-500`}>(${monthlyEquivalent}/month)</Text>
            )}
          </View>
        </View>
      </View>

      {/* Savings Highlight */}
      {isUpgradeToAnnual && annualSavings > 0 && (
        <View style={tw`mb-4 p-3 rounded-lg bg-green-100`}>
          <Text style={tw`text-sm text-green-800`}>
            💰 Save {annualSavings}% annually - that's ${Math.round(currentPlan.price.monthly * 12 - currentPlan.price.annual)} per year!
          </Text>
        </View>
      )}

      {/* Flexibility Note */}
      {isDowngradeToMonthly && (
        <View style={tw`mb-4 p-3 rounded-lg bg-blue-100`}>
          <Text style={tw`text-sm text-blue-800`}>
            🔄 Monthly billing gives you more flexibility to change or cancel anytime.
          </Text>
        </View>
      )}

      {/* Key Benefits */}
      <View style={tw`mb-4`}>
        <Text style={tw`text-sm font-medium text-gray-700 mb-2`}>
          {isUpgradeToAnnual ? '🎉 Annual Billing Benefits:' : '🔄 Monthly Billing Benefits:'}
        </Text>
        {isUpgradeToAnnual ? (
          <View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-green-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>Save {annualSavings}% compared to monthly</Text>
            </View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-green-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>{uiText('billingPerkYearly1')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-green-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>{uiText('billingPerkYearly2')}</Text>
            </View>
          </View>
        ) : (
          <View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-blue-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>{uiText('billingPerkMonthly1')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-blue-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>{uiText('billingPerkMonthly2')}</Text>
            </View>
            <View style={tw`flex-row items-center mb-1`}>
              <Text style={tw`text-blue-600 mr-2`}>✓</Text>
              <Text style={tw`text-sm text-gray-700 flex-1`}>{uiText('billingPerkMonthly3')}</Text>
            </View>
          </View>
        )}
      </View>

      {/* Action Button */}
      <TouchableOpacity
        style={tw`py-3 px-6 rounded-xl ${isUpgradeToAnnual ? 'bg-green-600' : 'bg-blue-600'} ${changing ? 'opacity-50' : ''}`}
        onPress={() => {
          console.log('🚀 [BillingIntervalCard] TouchableOpacity onPress triggered!');
          handleBillingChange();
        }}
        disabled={changing}
      >
        {changing ? (
          <View style={tw`flex-row items-center justify-center`}>
            <ActivityIndicator size="small" color="#FFFFFF" />
            <Text style={tw`text-white font-semibold ml-2`}>Updating...</Text>
          </View>
        ) : (
          <Text style={tw`text-white font-semibold text-center`}>
            {isUpgradeToAnnual ? '💰 Switch to Annual & Save' : '🔄 Switch to Monthly'}
          </Text>
        )}
      </TouchableOpacity>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await performBillingChange();
        }}
        title={`Switch to ${targetBillingInterval === 'annual' ? 'Annual' : 'Monthly'} Billing`}
        message={`${isUpgradeToAnnual
          ? `You'll save ${annualSavings}% (${Math.round(currentPlan.price.monthly * 12 - currentPlan.price.annual)} per year) with annual billing.\n\nYou'll be charged a prorated amount for the remaining period and then billed annually.`
          : 'You\'ll have more flexibility with monthly billing, but pay more overall.\n\nYou\'ll receive a prorated credit and then be billed monthly starting next cycle.'
        }`}
        confirmText="Continue to Payment"
        isLoading={changing}
        confirmButtonStyle={isUpgradeToAnnual ? 'success' : 'primary'}
        icon={isUpgradeToAnnual ? 'trending-up' : 'refresh'}
      />
    </View>
  );
};
